import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BillList } from '../components/bills/BillList';
import { BillForm } from '../components/bills/BillForm';
import { BillPaymentForm } from '../components/bills/BillPaymentForm';
import { Bill, BillFilters } from '../types/bill';

export const BillsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | undefined>();
  const [expandedBillId, setExpandedBillId] = useState<string | null>(null);
  const [filters, setFilters] = useState<BillFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
    jobId: undefined,
    vendorId: undefined
  });

  // If ID is in URL, expand that bill
  useEffect(() => {
    if (id) {
      setExpandedBillId(id);
    } else {
      setExpandedBillId(null);
    }
  }, [id]);

  const handleCreateBill = () => {
    setSelectedBill(undefined);
    setShowForm(true);
  };

  const handleEditBill = (bill: Bill) => {
    setSelectedBill(bill);
    setShowForm(true);
  };

  const handleViewBill = (bill: Bill) => {
    // Toggle expanded state - if already expanded, collapse it
    if (expandedBillId === bill.id) {
      setExpandedBillId(null);
      navigate('/bills');
    } else {
      setExpandedBillId(bill.id);
      navigate(`/bills/${bill.id}`);
    }
  };

  const handlePayBill = (bill: Bill) => {
    setSelectedBill(bill);
    setShowPaymentForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedBill(undefined);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedBill(undefined);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setSelectedBill(undefined);
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setSelectedBill(undefined);
  };

  const updateFilters = (newFilters: Partial<BillFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const inputStyle: React.CSSProperties = {
    padding: '9px 12px', background: '#f4efe2',
    border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
    fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
    outline: 'none', colorScheme: 'light' as any, width: '100%', boxSizing: 'border-box' as const,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
            Purchasing
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
            Bills
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
            Manage your vendor bills and expenses
          </div>
        </div>
        <button
          onClick={handleCreateBill}
          style={{
            fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #d97706, #b45309)',
            color: '#1c2033', border: 'none', padding: '12px 24px',
            borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 32px rgba(217,119,6,0.25)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Bill
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search bills..."
            value={filters.search || ''}
            onChange={(e) => updateFilters({ search: e.target.value })}
            style={{ ...inputStyle, flex: 1, minWidth: 180 }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(217,119,6,0.35)'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(92,78,51,0.25)'; }}
          />
          <select
            value={filters.status || ''}
            onChange={(e) => updateFilters({ status: e.target.value as any || undefined })}
            style={{ ...inputStyle, width: 160, cursor: 'pointer' }}
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="PAID">Paid</option>
            <option value="OVERDUE">Overdue</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => updateFilters({ dateFrom: e.target.value || undefined })}
            style={{ ...inputStyle, width: 160 }}
          />
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => updateFilters({ dateTo: e.target.value || undefined })}
            style={{ ...inputStyle, width: 160 }}
          />
          {(filters.search || filters.status || filters.dateFrom || filters.dateTo) && (
            <button
              onClick={() => setFilters({ page: 1, limit: 10 })}
              style={{
                padding: '9px 16px', background: 'none',
                border: '1px solid rgba(92,78,51,0.22)', borderRadius: 4,
                fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
                color: '#6b6a5c', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Bill List */}
      <BillList
        filters={filters}
        onView={handleViewBill}
        onEdit={handleEditBill}
        onPay={handlePayBill}
        expandedBillId={expandedBillId}
      />

      {/* Bill Form Modal */}
      {showForm && (
        <BillForm
          bill={selectedBill}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {/* Bill Payment Form Modal */}
      {showPaymentForm && selectedBill && (
        <BillPaymentForm
          billId={selectedBill.id}
          vendorId={selectedBill.vendorId}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      )}
    </div>
  );
};